import type { Fetcher } from '@/lib/api/fetcher';

import type {
  Credit,
  LedgerRow,
  Membership,
  MembershipPlan,
  MembershipPeriod,
  PrepaidPackage,
  Purchase,
  Wallet,
} from './schemas';
import {
  creditListSchema,
  ledgerRowSchema,
  membershipOrNullSchema,
  membershipSchema,
  packageListSchema,
  packageSchema,
  planListSchema,
  planSchema,
  purchaseSchema,
  redeemResultSchema,
  refundResultSchema,
  topUpResultSchema,
  walletSchema,
} from './schemas';

/** One function per BP-14 §5 route the §6 screens use. */
export interface PrepaidApi {
  listPackages: (includeInactive?: boolean) => Promise<PrepaidPackage[]>;
  createPackage: (body: PackageDraft) => Promise<PrepaidPackage>;
  setPackageActive: (packageId: string, active: boolean) => Promise<PrepaidPackage>;
  listPlans: (includeInactive?: boolean) => Promise<MembershipPlan[]>;
  createPlan: (body: PlanDraft) => Promise<MembershipPlan>;

  sellPackage: (clientId: string, packageId: string, outletId: string) => Promise<Purchase>;
  listCredits: (clientId: string) => Promise<Credit[]>;
  refundPackage: (clientId: string, purchaseId: string) => Promise<{ amount_paise: number }>;

  sellMembership: (clientId: string, planId: string, outletId: string) => Promise<Membership>;
  readMembership: (clientId: string) => Promise<Membership | null>;
  cancelMembership: (clientId: string, membershipId: string) => Promise<Membership>;

  readWallet: (clientId: string) => Promise<Wallet>;
  topUp: (
    clientId: string,
    amountPaise: number,
    outletId: string,
  ) => Promise<{ amount_paise: number; bonus_paise: number }>;
  adjust: (clientId: string, amountPaise: number, reason: string) => Promise<LedgerRow>;

  readEntitlements: (invoiceId: string) => Promise<Credit[]>;
  redeemCredit: (
    invoiceId: string,
    lineId: string,
    purchaseId: string,
  ) => Promise<{ total_paise: number }>;

  /** The client's own three, which name no id. */
  myWallet: () => Promise<Wallet>;
  myCredits: () => Promise<Credit[]>;
  myMembership: () => Promise<Membership | null>;
}

/** What `POST /packages` takes. Amounts are paise; quantities are strings. */
export interface PackageDraft {
  name: string;
  description?: string | null;
  price_paise: number;
  validity_days: number;
  sac_code: string;
  gst_rate_bp: number;
  items: { service_id: string; qty: string }[];
}

/** What `POST /membership-plans` takes. */
export interface PlanDraft {
  name: string;
  price_paise: number;
  period: MembershipPeriod;
  sac_code: string;
  gst_rate_bp: number;
  discount_bp: number;
  wallet_bonus_paise: number;
}

export const PREFIX = '/v1';

/** Build the §5 client over a `Fetcher`. */
export function createPrepaidApi(fetcher: Fetcher): PrepaidApi {
  const flag = (include: boolean | undefined): string =>
    include === true ? '?include_inactive=true' : '';
  return {
    listPackages: (includeInactive) =>
      fetcher.json(
        `${PREFIX}/packages${flag(includeInactive)}`,
        { method: 'GET' },
        packageListSchema,
      ),
    createPackage: (body) =>
      fetcher.json(`${PREFIX}/packages`, { method: 'POST', body }, packageSchema),
    setPackageActive: (packageId, active) =>
      fetcher.json(
        `${PREFIX}/packages/${packageId}?active=${String(active)}`,
        { method: 'PATCH' },
        packageSchema,
      ),
    listPlans: (includeInactive) =>
      fetcher.json(
        `${PREFIX}/membership-plans${flag(includeInactive)}`,
        { method: 'GET' },
        planListSchema,
      ),
    createPlan: (body) =>
      fetcher.json(`${PREFIX}/membership-plans`, { method: 'POST', body }, planSchema),

    sellPackage: (clientId, packageId, outletId) =>
      fetcher.json(
        `${PREFIX}/clients/${clientId}/packages`,
        { method: 'POST', body: { package_id: packageId, outlet_id: outletId } },
        purchaseSchema,
      ),
    listCredits: (clientId) =>
      fetcher.json(`${PREFIX}/clients/${clientId}/packages`, { method: 'GET' }, creditListSchema),
    refundPackage: (clientId, purchaseId) =>
      fetcher.json(
        `${PREFIX}/clients/${clientId}/packages/${purchaseId}/refund`,
        { method: 'POST', body: {} },
        refundResultSchema,
      ),

    sellMembership: (clientId, planId, outletId) =>
      fetcher.json(
        `${PREFIX}/clients/${clientId}/memberships`,
        { method: 'POST', body: { plan_id: planId, outlet_id: outletId } },
        membershipSchema,
      ),
    readMembership: (clientId) =>
      fetcher.json(
        `${PREFIX}/clients/${clientId}/memberships`,
        { method: 'GET' },
        membershipOrNullSchema,
      ),
    cancelMembership: (clientId, membershipId) =>
      fetcher.json(
        `${PREFIX}/clients/${clientId}/memberships/${membershipId}/cancel`,
        { method: 'POST', body: {} },
        membershipSchema,
      ),

    readWallet: (clientId) =>
      fetcher.json(`${PREFIX}/clients/${clientId}/wallet`, { method: 'GET' }, walletSchema),
    topUp: (clientId, amountPaise, outletId) =>
      fetcher.json(
        `${PREFIX}/clients/${clientId}/wallet/topup`,
        { method: 'POST', body: { amount_paise: amountPaise, outlet_id: outletId } },
        topUpResultSchema,
      ),
    adjust: (clientId, amountPaise, reason) =>
      fetcher.json(
        `${PREFIX}/clients/${clientId}/wallet/adjust`,
        { method: 'POST', body: { amount_paise: amountPaise, reason } },
        ledgerRowSchema,
      ),

    readEntitlements: (invoiceId) =>
      fetcher.json(
        `${PREFIX}/invoices/${invoiceId}/entitlements`,
        { method: 'GET' },
        creditListSchema,
      ),
    redeemCredit: (invoiceId, lineId, purchaseId) =>
      fetcher.json(
        `${PREFIX}/invoices/${invoiceId}/redeem-credit`,
        { method: 'POST', body: { line_id: lineId, purchase_id: purchaseId } },
        redeemResultSchema,
      ),

    myWallet: () => fetcher.json(`${PREFIX}/me/wallet`, { method: 'GET' }, walletSchema),
    myCredits: () => fetcher.json(`${PREFIX}/me/packages`, { method: 'GET' }, creditListSchema),
    myMembership: () =>
      fetcher.json(`${PREFIX}/me/memberships`, { method: 'GET' }, membershipOrNullSchema),
  };
}
